import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { path, pathOr } from 'ramda';
import { useFormContext } from 'react-hook-form';
import { Column, Row } from 'components/shared/Layout';
import TextInput from 'components/ui/TextInput';
import Select from 'components/ui/Select';
import { pnrRegex, voucherReferenceReg } from 'utils/validators';
import currencySymbols from 'constants/currencies';
import { voucherCodes, voucherTypes, minMaxValues, replaceVoucherErrorCodes } from 'constants/vouchers';
import { StyledRow, StyledColumn, StyledSpan, ErrorMessage, StyledLabel, StyledTitle } from 'pages/CreateVoucher/CreateVoucher.styles';

const VoucherDetails = ({
  errors,
  voucherType,
  currency,
  allowedCurrencies,
  oneYearFromToday,
  allowedVoucherTypes,
  defaultValues = {}
}) => {
  const { register } = useFormContext();

  const replaceVoucherErrorId = replaceVoucherErrorCodes[path(['graphQLErrors', [0], 'code'], errors.voucherToReplaceAPIError)] || '';
  const invalidFields = {
    ...pathOr({}, ['graphQLErrors', [0], 'invalidArgs'], errors.voucherToReplaceAPIError)
  };
  if (invalidFields.currency) {
    invalidFields.currency = currencySymbols[invalidFields.currency];
  }

  return (
    <>
      <StyledTitle>
        <FormattedMessage id="createVoucher.voucherDetails" />
      </StyledTitle>
      <StyledRow columns={12}>
        <Column alignItems="flex-start" flexDirection="column" span={3}>
          <StyledLabel htmlFor="voucherType">
            <FormattedMessage id="createVoucher.fields.voucherType" />
          </StyledLabel>
          <Select
            {...register('voucherType', { required: true })}
            id="voucherType"
            data-cy="voucher-type"
            defaultValue={defaultValues.voucherType ?? allowedVoucherTypes[0]}
          >
            {allowedVoucherTypes.map((voucherCode) => (
              <option key={voucherCode} value={voucherCode}>
                {voucherTypes[voucherCode]}
              </option>
            ))}
          </Select>
        </Column>

        <Row columns={12}>
          <StyledColumn alignItems="flex-start" flexDirection="column" span={3}>
            {voucherCodes.REV !== voucherType && (
              <>
                <StyledLabel htmlFor="currency">
                  <FormattedMessage id="createVoucher.fields.currency" />
                </StyledLabel>
                <Select
                  data-cy="currency"
                  {...register('currency', { required: true })}
                  id="currency"
                  defaultValue={defaultValues.currency ?? allowedCurrencies[0]}
                >
                  {Object.entries(currencySymbols)
                    .filter(([currencyType]) => allowedCurrencies.includes(currencyType))
                    .map(([currencyType, symbol]) => (
                      <option key={currencyType} value={currencyType}>
                        {currencyType} - {symbol}
                      </option>
                    ))}
                </Select>
              </>
            )}
            {voucherCodes.REV === voucherType && (
              <>
                <StyledLabel htmlFor="voucherToReplace">
                  <FormattedMessage id="createVoucher.fields.associatedVoucher" />
                </StyledLabel>
                <TextInput
                  data-cy="voucher-to-replace"
                  {...register('voucherToReplace', {
                    required: true,
                    pattern: voucherReferenceReg
                  })}
                  width="100%"
                  id="voucherToReplace"
                  defaultValue={defaultValues.associatedPNR}
                />
                {errors.voucherToReplace && (
                  <ErrorMessage>
                    <FormattedMessage id="payments.voucherIsNotANumber" />
                  </ErrorMessage>
                )}
              </>
            )}
          </StyledColumn>

          <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={5}>
            <StyledLabel htmlFor="amount">
              <FormattedMessage id="createVoucher.fields.amount" />
            </StyledLabel>
            <TextInput
              data-cy="voucher-amount"
              data-id="voucher-amount"
              type="number"
              width="100%"
              {...register('amount', {
                required: true,
                min: minMaxValues[voucherType]?.min,
                max: minMaxValues[voucherType]?.max
              })}
              id="amount"
              step=".01"
              placeholder="0.0"
              defaultValue={defaultValues.amount}
            />
            <StyledSpan error={errors.amount} data-cy="amount-range">
              <FormattedMessage
                id="createVoucher.voucherAmount"
                defaultMessage={`Enter an amount from {currency}{voucherMin} up to {currency}{voucherMax}`}
                values={{
                  voucherMin: minMaxValues[voucherType]?.min,
                  voucherMax: minMaxValues[voucherType]?.max,
                  currency: currencySymbols[currency]
                }}
              />
            </StyledSpan>
          </StyledColumn>

          {voucherCodes.REV !== voucherType && (
            <StyledColumn alignItems="flex-start" flexDirection="column" span={3} start={9}>
              <StyledLabel htmlFor="salesDateEnd">
                <FormattedMessage id="createVoucher.fields.salesDateEnd" />
              </StyledLabel>
              <TextInput
                data-cy="sales-date-end"
                type="date"
                {...register('salesDateEnd', { required: true })}
                id="salesDateEnd"
                min={moment().format('YYYY-MM-DD').toString()}
                max={oneYearFromToday}
                defaultValue={defaultValues.salesDateEnd || oneYearFromToday}
              />
            </StyledColumn>
          )}
        </Row>

        {replaceVoucherErrorId && (
          <Column alignItems="flex-start" flexDirection="column" span={12} data-cy="replace-voucher-error">
            <ErrorMessage>
              <FormattedMessage id={replaceVoucherErrorId} values={invalidFields} />
            </ErrorMessage>
          </Column>
        )}

        <Column alignItems="flex-start" flexDirection="column" span={3}>
          {[voucherCodes.SFV, voucherCodes.VGV].includes(voucherType) && (
            <>
              <StyledLabel htmlFor="associatedPNR">
                <FormattedMessage id="createVoucher.fields.associatedPNR" />
              </StyledLabel>
              <TextInput
                data-cy="associated-pnr"
                {...register('associatedPNR', { required: true, pattern: pnrRegex })}
                width="100%"
                id="associatedPNR"
                defaultValue={defaultValues.associatedPNR}
              />
              {errors.associatedPNR && (
                <ErrorMessage>
                  <FormattedMessage id="extrasOnlyBookings.errors.pnr" />
                </ErrorMessage>
              )}
            </>
          )}
        </Column>
      </StyledRow>
    </>
  );
};

VoucherDetails.propTypes = {
  errors: PropTypes.shape({
    associatedPNR: PropTypes.bool,
    voucherToReplace: PropTypes.bool,
    voucherToReplaceAPIError: PropTypes.object,
    amount: PropTypes.bool
  }),
  voucherType: PropTypes.oneOf(Object.keys(voucherTypes).map((v) => v)),
  currency: PropTypes.oneOf(Object.keys(currencySymbols).map((v) => v)),
  allowedCurrencies: PropTypes.arrayOf(PropTypes.string),
  oneYearFromToday: PropTypes.string.isRequired,
  allowedVoucherTypes: PropTypes.arrayOf(PropTypes.string),
  defaultValues: PropTypes.shape({
    voucherType: PropTypes.string,
    currency: PropTypes.string,
    amount: PropTypes.string,
    salesDateEnd: PropTypes.string,
    associatedPNR: PropTypes.string
  })
};

export default VoucherDetails;
